import type { DocumentFieldDefinition } from '~/lib/document_field';
import { type MouseEvent, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Card } from 'primereact/card';
import { FormLabelInGrid } from '~/components/forms/FormLabelInGrid';
import { Dropdown } from 'primereact/dropdown';
import { BqFormErrorText } from '~/components/ui/misc/bqTag/BqFormErrorText';
import { InputTextarea } from 'primereact/inputtextarea';
import * as React from 'react';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';

export type FormFieldCorrection = {
  fieldValue: string;
  fieldId: FieldIdObj;
};
export type FieldIdObj = {
  id: string;
  name: string;
};
export function FieldCorrectionForm(props: {
  defaultText: string | undefined;
  fieldTypes: DocumentFieldDefinition[];
  close: () => void;
  onSubmit: (
    fieldCorrection: FormFieldCorrection,
  ) => Promise<{ success: boolean }> | void;
}) {
  const [isError] = useState(false);
  const { control, handleSubmit } = useForm<FormFieldCorrection>({
    defaultValues: { fieldValue: '', fieldId: { id: '', name: '' } },
  });
  // const fetcher = useFetcher<{ success: boolean }>();
  const fieldDropdownOptions = props.fieldTypes.map((ft) => ({
    value: ft.id,
    label: ft.name,
  }));
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: See note
    <div
      className="card"
      // There's some silly behaviour where the antd Select
      // has placed itself of in nowhere-land at the end of <body>
      // which in turn makes clicking it hit the mousedown handler
      // of the react pdf highlighter which closes the tip because
      // the target is not the descendant of a pdf highlighter container.
      // Weird behaviour on both fronts, we're stuck in the middle doing
      // our best.
      onClick={handleStopPropagation}
      onPointerDown={handleStopPropagation}
      onMouseDown={handleStopPropagation}
    >
      <Card
        title={'Update field value'}
        className="w-20rem"
        style={{ width: '50vw' }}
      >
        {/*<h4>Update field value</h4>*/}
        <form
          onSubmit={handleSubmit(props.onSubmit)}
          className="flex flex-column gap-2"
        >
          <div className="flex-column flex h-full">
            <div className="grid">
              <Controller
                name="fieldId"
                control={control}
                rules={{ required: 'Field Name - is required.' }}
                render={({ field, fieldState }) => (
                  <>
                    <FormLabelInGrid
                      required
                      htmlFor={field.name}
                      label="Please select field to change"
                      classes="col col-12 text-left"
                    />
                    <div className="col col-12 text-left">
                      <Dropdown
                        id={field.name}
                        value={field.value.id}
                        className="w-16rem"
                        filter
                        options={fieldDropdownOptions}
                        onChange={(e) =>
                          field.onChange({
                            id: e.value,
                            name:
                              fieldDropdownOptions.find(
                                (option) => option.value === e.value,
                              )?.label || '',
                          })
                        }
                      />
                      <BqFormErrorText error={fieldState.error?.message} />
                    </div>
                  </>
                )}
              />
              <Controller
                name="fieldValue"
                control={control}
                rules={{ required: 'Value is required.' }}
                render={({ field, fieldState }) => (
                  <>
                    <FormLabelInGrid
                      required
                      htmlFor={field.name}
                      label="Enter value for field"
                      classes="col col-12 text-left"
                    />
                    <div className="col col-12 text-left">
                      <InputTextarea
                        id={field.name}
                        value={field.value}
                        className="w-16rem"
                        onChange={(e) => {
                          field.onChange(e.target.value);
                        }}
                      />
                      <BqFormErrorText error={fieldState.error?.message} />
                    </div>
                  </>
                )}
              />

              <div className="col col-9 text-left">
                <Button type="submit">Save</Button>
              </div>
            </div>
          </div>
        </form>
        {isError ? (
          <Message severity="error" text="Error updating field" />
        ) : null}
      </Card>
    </div>
  );
}
function handleStopPropagation(e: MouseEvent) {
  e.stopPropagation();
}
